import { Button, Checkbox, DatePicker, message, Modal, Popconfirm, Select, Space, Spin, Table, Tag } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DatabaseOutlined,
  FilterOutlined,
  ReloadOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { serverFetch } from "@server";
import { strDate2Server } from "@extensions/convert";
import { fmtDateTimeFull } from "@extensions/utils";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "../orders/order.css";
import "../records/records.css";
import "./packets.css";

const vidOptions = [
  { value: 0, label: "Непринятые" },
  { value: 1, label: "За период" },
];

const directOptions = [
  { value: 1, label: "Входящие" },
  { value: -1, label: "Исходящие" },
];

const statusLabels = {
  0: "Новый",
  1: "Принят",
  2: "Удален",
};

const statusOptions = [
  { value: 0, label: "Новый" },
  { value: 1, label: "Принят" },
  { value: 2, label: "Удален" },
];

const renderStatus = value => statusLabels[value] || "";

const statusTagColor = s => {
  if (s === 0) return "processing";
  if (s === 1) return "success";
  if (s === 2) return "error";
  return "default";
};

const Packets = () => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [filter, setFilter] = useState({ vid: 0, dateBeg: dayjs(), dateEnd: dayjs(), direction: 1 });
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftVid, setDraftVid] = useState(0);
  const [draftDateBeg, setDraftDateBeg] = useState(() => dayjs());
  const [draftDateEnd, setDraftDateEnd] = useState(() => dayjs());
  const [draftDirection, setDraftDirection] = useState(1);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editStatus, setEditStatus] = useState(0);

  const fetchPackets = f => {
    setLoading(true);
    serverFetch(
      `packet?vid=${f.vid}&dateBeg=${strDate2Server(f.dateBeg)}&dateEnd=${strDate2Server(f.dateEnd)}&direction=${f.direction}`
    )
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const getData = () => fetchPackets(filter);

  useEffect(() => {
    fetchPackets(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- первичная загрузка
  }, []);

  const filterSummary = useMemo(() => {
    const vidLabel = vidOptions.find(v => v.value === filter.vid)?.label ?? String(filter.vid);
    const parts = [vidLabel];
    if (filter.vid) {
      parts.push(`${filter.dateBeg.format("DD.MM.YYYY")} — ${filter.dateEnd.format("DD.MM.YYYY")}`);
    }
    const dirLabel = directOptions.find(d => d.value === filter.direction)?.label ?? String(filter.direction);
    parts.push(dirLabel);
    return parts.join(" · ");
  }, [filter]);

  const openFilterModal = () => {
    setDraftVid(filter.vid);
    setDraftDateBeg(filter.dateBeg);
    setDraftDateEnd(filter.dateEnd);
    setDraftDirection(filter.direction);
    setFilterOpen(true);
  };

  const applyFilter = () => {
    if (draftVid && (!draftDateBeg || !draftDateEnd)) {
      message.warning("Укажите период дат");
      return;
    }
    const next = {
      vid: draftVid,
      dateBeg: draftDateBeg,
      dateEnd: draftDateEnd,
      direction: draftDirection,
    };
    setFilter(next);
    setFilterOpen(false);
    fetchPackets(next);
  };

  const cancelFilter = () => setFilterOpen(false);

  const handleEditStatus = () => {
    if (selected.length === 0) return message.warning("Выберите пакеты для изменения статуса");
    const firstPacket = items.find(item => item.id === selected[0]);
    if (firstPacket) {
      setEditStatus(firstPacket.status);
      setEditModalVisible(true);
    }
  };

  const handleSaveStatus = () => {
    setLoading(true);
    const promises = selected.map(id =>
      serverFetch(`packet/${id}`, {
        method: "PUT",
        bodyData: { status: editStatus, loadDateTime: dayjs().toISOString() },
      })
    );
    Promise.allSettled(promises).then(results => {
      const succeeded = results.filter(r => r.status === "fulfilled").length;
      const failed = results.filter(r => r.status === "rejected").length;
      if (succeeded > 0) message.success(`Обновлено пакетов: ${succeeded}`);
      if (failed > 0) message.error(`Ошибок: ${failed}`);
      setEditModalVisible(false);
      getData();
    });
  };

  const togglePacketSelection = id => {
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const handleDelete = () => {
    setLoading(true);
    const promises = selected.map(id => serverFetch(`packet/${id}`, { method: "DELETE" }));
    Promise.allSettled(promises).then(results => {
      const succeeded = results.filter(r => r.status === "fulfilled").length;
      const failed = results.filter(r => r.status === "rejected").length;
      if (succeeded > 0) message.success(`Удалено пакетов: ${succeeded}`);
      if (failed > 0) message.error(`Ошибок: ${failed}`);
      setSelected([]);
      getData();
    });
  };

  const columns = [
    { dataIndex: "id", title: "#" },
    { dataIndex: "companyName", title: "Компания" },
    { dataIndex: "dateTime", title: "Отправлен", render: fmtDateTimeFull },
    { dataIndex: "status", title: "Статус", render: renderStatus },
    { dataIndex: "loadDateTime", title: "Принят", render: fmtDateTimeFull },
    { dataIndex: "recipeDbId", title: "База" },
  ];

  const onFilterChange = value => setFilter({ ...filter, ...value });

  const filterToolbarDesktop = (
    <Space style={{ marginBottom: 16 }}>
      <div>Вид</div>
      <Select options={vidOptions} value={filter.vid} onChange={e => onFilterChange({ vid: e })} style={{ width: 150 }} />
      {filter.vid ? (
        <>
          <DatePicker format="DD.MM.YYYY" value={filter.dateBeg} onChange={e => onFilterChange({ dateBeg: e })} allowClear={false} />
          <div>-</div>
          <DatePicker format="DD.MM.YYYY" value={filter.dateEnd} onChange={e => onFilterChange({ dateEnd: e })} allowClear={false} />
        </>
      ) : null}
      <Select options={directOptions} value={filter.direction} onChange={e => onFilterChange({ direction: e })} style={{ width: 150 }} />
      <Button onClick={getData}>Обновить</Button>
    </Space>
  );

  const actionsRow = (
    <Space style={{ marginBottom: 16 }} wrap>
      <Button onClick={handleEditStatus} disabled={selected.length === 0 || loading}>
        Изменить статус {selected.length > 0 && `(${selected.length})`}
      </Button>
      <Popconfirm
        title="Подтверждение удаления"
        description={`Вы уверены, что хотите удалить ${selected.length === 1 ? "этот пакет" : `пакетов: ${selected.length}`}?`}
        onConfirm={handleDelete}
        okText="Удалить"
        cancelText="Отмена"
        disabled={selected.length === 0}
      >
        <Button danger disabled={selected.length === 0 || loading}>
          Удалить {selected.length > 0 && `(${selected.length})`}
        </Button>
      </Popconfirm>
    </Space>
  );

  const tableEl = (
    <Table
      className="packets-table"
      dataSource={items}
      loading={loading}
      columns={columns}
      pagination={false}
      size="small"
      rowKey="id"
      rowSelection={{ type: "checkbox", selectedRowKeys: selected, onChange: setSelected }}
      scroll={{ x: "max-content", y: "calc(100vh - 200px)" }}
    />
  );

  const mobileListEl = (
    <Spin spinning={loading}>
      <div className="packets-mobile-list">
        {items.map(record => {
          const isChecked = selected.includes(record.id);
          const hasCompany = Boolean(record.companyName);
          const hasBase = record.recipeDbId != null && record.recipeDbId !== "";
          return (
            <div
              key={record.id}
              role="button"
              tabIndex={0}
              className="packets-mobile-card"
              onClick={() => togglePacketSelection(record.id)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  togglePacketSelection(record.id);
                }
              }}
              aria-pressed={isChecked}
              aria-label={`Пакет #${record.id}, выбрать`}
            >
              <div className="packets-mobile-card-row1">
                <div className="packets-mobile-card-row1-left">
                  <Checkbox checked={isChecked} className="packets-mobile-card-checkbox" tabIndex={-1} />
                  <span className="packets-mobile-card-id">#{record.id}</span>
                </div>
                <Tag className="packets-mobile-card-status" color={statusTagColor(record.status)}>
                  {renderStatus(record.status)}
                </Tag>
              </div>
              <div className="packets-mobile-card-row2">
                <span className="packets-mobile-card-sent">{fmtDateTimeFull(record.dateTime) || "—"}</span>
                <RightOutlined className="packets-mobile-card-row2-arrow" aria-hidden />
                <span className="packets-mobile-card-accepted">{fmtDateTimeFull(record.loadDateTime) || "—"}</span>
              </div>
              <div className="packets-mobile-card-row3">
                {hasCompany ? <span>{record.companyName}</span> : null}
                {hasCompany && hasBase ? (
                  <span className="packets-mobile-card-row3-sep" aria-hidden>
                    {" "}
                    ·{" "}
                  </span>
                ) : null}
                {hasBase ? (
                  <span className="packets-mobile-card-base">
                    <DatabaseOutlined className="packets-mobile-card-base-icon" aria-hidden />
                    {record.recipeDbId}
                  </span>
                ) : null}
                {!hasCompany && !hasBase ? "—" : null}
              </div>
            </div>
          );
        })}
      </div>
    </Spin>
  );

  return (
    <div className={isMobile ? "page orders-page--mobile" : "page packets-page--desktop"}>
      {isMobile ? (
        <>
          <div className="orders-mobile-toolbar">
            <div className="company-backups-mobile-filter-row task-plans-mobile-filter-row">
              <span className="company-backups-mobile-filter-summary" title={filterSummary}>
                {filterSummary}
              </span>
              <Button
                type="default"
                icon={<FilterOutlined />}
                onClick={openFilterModal}
                aria-label="Фильтр"
                className="company-backups-mobile-filter-icon-btn"
              />
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={getData}
                loading={loading}
                aria-label="Обновить"
                className="company-backups-mobile-refresh-btn"
              />
            </div>
          </div>
          {filterOpen && (
            <div
              className="misoft-modal-overlay company-backups-filter-overlay--top"
              onClick={e => e.target === e.currentTarget && cancelFilter()}
            >
              <div
                className="misoft-modal-content company-backups-filter-sheet--top"
                onClick={e => e.stopPropagation()}
              >
                <div className="company-base-sheet-header">
                  <span className="company-base-sheet-title">Фильтр</span>
                  <Space size="small" className="company-base-sheet-actions">
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={applyFilter}
                      aria-label="Применить"
                      className="company-base-header-icon-btn"
                    />
                    <Button
                      type="default"
                      icon={<CloseOutlined />}
                      onClick={cancelFilter}
                      aria-label="Отмена"
                      className="company-base-header-icon-btn"
                    />
                  </Space>
                </div>
                <div className="company-base-sheet-body company-backups-filter-sheet-body">
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">Вид</span>
                    <Select options={vidOptions} value={draftVid} onChange={setDraftVid} style={{ width: "100%" }} />
                  </div>
                  {draftVid ? (
                    <>
                      <div className="company-backups-filter-field">
                        <span className="company-backups-filter-label">С даты</span>
                        <DatePicker
                          value={draftDateBeg}
                          onChange={setDraftDateBeg}
                          format="DD.MM.YYYY"
                          allowClear={false}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div className="company-backups-filter-field">
                        <span className="company-backups-filter-label">По дату</span>
                        <DatePicker
                          value={draftDateEnd}
                          onChange={setDraftDateEnd}
                          format="DD.MM.YYYY"
                          allowClear={false}
                          style={{ width: "100%" }}
                        />
                      </div>
                    </>
                  ) : null}
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">Направление</span>
                    <Select options={directOptions} value={draftDirection} onChange={setDraftDirection} style={{ width: "100%" }} />
                  </div>
                </div>
              </div>
            </div>
          )}
          {actionsRow}
          <div className="orders-mobile-scroll">{mobileListEl}</div>
        </>
      ) : (
        <>
          {filterToolbarDesktop}
          {actionsRow}
          <div className="packets-desktop-table-wrap">{tableEl}</div>
        </>
      )}
      <Modal
        title={`Изменение статуса ${selected.length === 1 ? "пакета" : `пакетов (${selected.length})`}`}
        open={editModalVisible}
        onOk={handleSaveStatus}
        onCancel={() => setEditModalVisible(false)}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={loading}
      >
        <div style={{ marginTop: 16 }}>
          <label>Новый статус:</label>
          <Select options={statusOptions} value={editStatus} onChange={setEditStatus} style={{ width: "100%", marginTop: 8 }} />
        </div>
      </Modal>
    </div>
  );
};

export default Packets;
